import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { ResponsiveMenuItem } from 'components/DropdownButtons/_utils/ResponsiveMenuItem'
import type { StackedChartSection } from 'components/StackedChart/types'
import { translate } from 'i18n/i18n'
import { cloneDeep } from 'lodash'
import { detailsPanelStore } from 'stores/detailsPanelStore/detailsPanelStore'
import type { TBlock } from 'stores/fieldAssetStore/helperTypes'
import { selectedPropertyHasPermission } from '../../_utils/selectedPropertyHasPermission'
import { AlternariaSettingsModal } from './AlternariaSettingsModal'

const permission = 'EDIT_ALTERNARIA_DSV_THRESHOLD'

type AlternariaSettingDropdownProps = {
  isSummary: boolean
  data?: routes.Values.Response
  blocks?: Record<number, TBlock>
  blockId?: string
  updateData: (pathToSet: string, dataToSet: unknown) => void
}

export const AlternariaSettingsTitleLink = ({
  isSummary,
  data,
  blocks,
  blockId,
  updateData,
}: AlternariaSettingDropdownProps) => {
  const titleLink: NonNullable<StackedChartSection['titleLink']> = []

  if (!selectedPropertyHasPermission({ permission })) {
    return titleLink
  }

  titleLink.push(
    <>
      <ResponsiveMenuItem
        key="spray-thresholds-menu-item"
        data-role="menu-item"
        onClick={() => {
          detailsPanelStore.setState((prev) => {
            return {
              ...prev,
              alternariaSettingsOpened: true,
            }
          })
        }}
      >
        {translate.phrases.banyanApp('Edit Spray Thresholds')}
      </ResponsiveMenuItem>
      {isSummary ? (
        <AlternariaSettingsModal
          key="spray-thresholds-modal"
          data-role="modal"
          handleAfterSave={(blockIdToNewThreshold) => {
            const blocksToUpdate = Object.keys(blocks ?? {}).filter(
              (blockId) => blockIdToNewThreshold[Number(blockId)],
            )

            if (blocksToUpdate.length === 0) return

            const oldData = cloneDeep(data?.blocks) ?? {}

            blocksToUpdate.forEach((blockId) => {
              const valuesForBlock = data?.blocks?.[blockId]?.values?.alternariaCumulativeRolling7Days?.[0]

              if (!valuesForBlock) return

              const updated = {
                ...valuesForBlock,
                metadata: {
                  ...valuesForBlock.metadata,
                  blockDSVSprayThreshold: blockIdToNewThreshold[Number(blockId)],
                },
              }

              oldData[Number(blockId)] = {
                ...oldData[Number(blockId)],
                values: {
                  ...(oldData[Number(blockId)]?.values ?? {}),
                  alternariaCumulativeRolling7Days: [updated],
                },
              }
            })

            updateData('blocks', oldData)
          }}
        />
      ) : (
        <AlternariaSettingsModal
          key="spray-thresholds-modal"
          data-role="modal"
          handleAfterSave={(blockIdToNewThreshold) => {
            if (blockIdToNewThreshold[Number(blockId)]) {
              const path =
                'blocks.' +
                blockId +
                '.values.alternariaCumulativeRolling7Days.0.metadata.blockDSVSprayThreshold'

              updateData(path, blockIdToNewThreshold[Number(blockId)])
            }
          }}
        />
      )}
    </>,
  )

  return titleLink
}
